/**
 * Icone giocatori (Admin · App, Andrea 2026-10-01): si cerca un giocatore del
 * listone, si apre il generatore (il suo generatore-icone-giocatori.html in un
 * iframe), si crea l'icona e si salva assegnata a quel giocatore. L'app la
 * mostra ovunque al posto dell'icona statica.
 *
 * Protocollo postMessage (stessa origine):
 * - icona:ready    generatore → app
 * - icona:prefill  app → generatore: { titolo, stato } (scelte salvate)
 * - icona:save     generatore → app: { image: PNG 256 data-URL, stato }
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  useEliminaIconaGiocatore,
  useIconaGiocatore,
  useIconeGiocatori,
  useSalvaIconaGiocatore,
} from "../../hooks/useIconeGiocatori";
import { api } from "../../lib/api";
import { haIconaStatica, PlayerAvatar } from "../common/PlayerAvatar";
import { siglaRuolo, sigleRuoli } from "../common/nomenclatura";
import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";

interface GiocatoreRiga {
  id: string;
  nome: string;
  club_serie_a: string | null;
  ruolo_classic: string | null;
  ruoli_mantra: string[] | null;
  stato: string;
}

interface Risposta {
  items: GiocatoreRiga[];
  total: number;
}

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? tr("Errore");
}

function useDebounce<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function IconeGiocatoriPage() {
  const { user } = useAuth();
  const [testo, setTesto] = useState("");
  const ricerca = useDebounce(testo.trim(), 300);
  const [scelto, setScelto] = useState<GiocatoreRiga | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  const { data: icone } = useIconeGiocatori();
  const elimina = useEliminaIconaGiocatore();

  const { data, isFetching } = useQuery({
    queryKey: ["icone-giocatori-ricerca", ricerca],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "40", stato: "tutti", sort: "nome" });
      if (ricerca) params.set("search", ricerca);
      return (await api.get<Risposta>(`/giocatori?${params}`)).data;
    },
    enabled: ricerca.length >= 2,
  });

  if (!user?.is_admin_app) {
    return (
      <MobileShell logoSuffix="ICONE">
        <div className="card text-sm text-fg-muted">
          {tr("Le icone dei giocatori le gestisce l'amministratore dell'app.")}
        </div>
      </MobileShell>
    );
  }

  const stato = (g: GiocatoreRiga) =>
    icone?.has(g.nome)
      ? { testo: tr("In app"), cls: "bg-success/20 text-success" }
      : haIconaStatica(g.nome)
        ? { testo: tr("Statica"), cls: "bg-info/20 text-info" }
        : { testo: tr("Generica"), cls: "bg-fg-muted/20 text-fg-muted" };

  const togli = async (g: GiocatoreRiga) => {
    if (
      !confirm(
        tr("Togliere l'icona fatta in app a {{v1}}? Tornera' quella statica, se c'e'.", {
          v1: g.nome,
        }),
      )
    )
      return;
    setEsito(null);
    try {
      await elimina.mutateAsync(g.id);
      setEsito(tr("Icona di {{v1}} tolta", { v1: g.nome }));
    } catch (e) {
      setEsito(tr("⚠ {{v1}}", { v1: errMsg(e) }));
    }
  };

  return (
    <MobileShell logoSuffix="ICONE">
      <div className="card space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
          {tr("Icone giocatori")}
        </div>
        <div className="text-[11px] text-fg-muted">
          Cerca il giocatore, apri il generatore, crea l'icona e salvala: l'app la mostra subito
          ovunque, per tutte le leghe. Chi non ha un'icona fatta qui tiene quella statica.
          {icone && (
            <>
              {" "}
              Icone fatte in app: <b className="text-fg">{icone.size}</b>.
            </>
          )}
        </div>
        <input
          className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder={tr("Cerca per nome (almeno 2 lettere)…")}
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          autoFocus
        />
        {esito && <div className="text-xs text-fg">{esito}</div>}
      </div>

      {ricerca.length >= 2 && (
        <div className="card p-0 overflow-hidden">
          {isFetching && !data && (
            <div className="p-4 text-sm text-fg-muted text-center">{tr("Cerco…")}</div>
          )}
          {data && data.items.length === 0 && (
            <div className="p-4 text-sm text-fg-muted text-center">
              {tr("Nessun giocatore trovato.")}
            </div>
          )}
          {data?.items.map((g) => {
            const st = stato(g);
            const inApp = icone?.has(g.nome);
            return (
              <div
                key={g.id}
                className="flex items-center gap-3 px-3 py-2 border-b border-primary/10 last:border-b-0"
              >
                <PlayerAvatar nome={g.nome} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-fg truncate">{g.nome}</div>
                  <div className="text-[11px] text-fg-muted truncate">
                    {g.club_serie_a ?? "—"} ·{" "}
                    {g.ruoli_mantra?.length
                      ? sigleRuoli(g.ruoli_mantra)
                      : siglaRuolo(g.ruolo_classic)}
                    {g.stato !== "serie_a" && <> · {g.stato}</>}
                  </div>
                </div>
                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${st.cls}`}>
                  {st.testo}
                </span>
                <button
                  type="button"
                  className="btn-primary text-xs px-3 py-1.5"
                  onClick={() => setScelto(g)}
                >
                  {inApp ? tr("Modifica") : tr("Crea")}
                </button>
                {inApp && (
                  <button
                    type="button"
                    className="text-[10px] uppercase tracking-wider text-danger hover:underline disabled:opacity-50"
                    disabled={elimina.isPending}
                    onClick={() => togli(g)}
                  >
                    {tr("Togli")}
                  </button>
                )}
              </div>
            );
          })}
          {data && data.total > data.items.length && (
            <div className="p-2 text-[10px] text-fg-muted text-center">
              Mostro {data.items.length} di {data.total}: affina la ricerca.
            </div>
          )}
        </div>
      )}

      {scelto && (
        <GeneratoreIconaModal
          giocatore={scelto}
          onClose={() => setScelto(null)}
          onSalvato={() => setEsito(tr("Icona di {{v1}} salvata", { v1: scelto.nome }))}
        />
      )}
    </MobileShell>
  );
}

function GeneratoreIconaModal({
  giocatore,
  onClose,
  onSalvato,
}: {
  giocatore: GiocatoreRiga;
  onClose: () => void;
  onSalvato: () => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [stato, setStato] = useState<"pronto" | "salvo" | "fatto" | "errore">("pronto");
  const [errore, setErrore] = useState<string | null>(null);
  const salva = useSalvaIconaGiocatore();
  // Le scelte salvate, se l'icona esiste gia' (404 = prima icona).
  const { data: esistente, isLoading } = useIconaGiocatore(giocatore.id);
  const prontoRef = useRef(false);

  const prefill = () => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: "icona:prefill",
        payload: {
          titolo: `Icona di ${giocatore.nome}${giocatore.club_serie_a ? ` · ${giocatore.club_serie_a}` : ""}`,
          stato: esistente?.config ?? null,
        },
      },
      window.location.origin,
    );
  };

  // Il prefill parte quando SIA il generatore SIA le scelte salvate sono pronti.
  useEffect(() => {
    if (prontoRef.current && !isLoading) prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, esistente]);

  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const msg = e.data as {
        type?: string;
        payload?: { image?: string; stato?: Record<string, unknown> };
      } | null;
      if (!msg?.type) return;
      if (msg.type === "icona:ready") {
        prontoRef.current = true;
        if (!isLoading) prefill();
        return;
      }
      if (msg.type === "icona:save" && msg.payload?.image) {
        setStato("salvo");
        setErrore(null);
        try {
          await salva.mutateAsync({
            giocatoreId: giocatore.id,
            pngDataUrl: msg.payload.image,
            config: msg.payload.stato ?? null,
          });
          setStato("fatto");
          onSalvato();
        } catch (err) {
          setErrore(errMsg(err));
          setStato("errore");
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giocatore.id, isLoading, esistente]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-primary-deep border-b border-accent/30">
        <div className="min-w-0 flex items-center gap-2">
          <PlayerAvatar nome={giocatore.nome} size={32} />
          <div className="min-w-0">
            <div className="font-display text-accent text-base leading-tight truncate">
              {giocatore.nome}
              {giocatore.club_serie_a && (
                <span className="text-fg-muted text-xs"> · {giocatore.club_serie_a}</span>
              )}
            </div>
            <div className="text-[10px] text-fg-muted">
              {stato === "pronto" && tr("Componi l'icona e premi «Salva icona»")}
              {stato === "salvo" && tr("Salvo l'icona…")}
              {stato === "fatto" && tr("✓ Icona salvata e assegnata. Puoi chiudere o rifarla.")}
              {stato === "errore" && tr("⚠ {{v1}}", { v1: errore })}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 px-3 py-1.5 rounded-card border border-accent/40 text-accent text-sm hover:bg-accent/10"
        >
          {stato === "fatto" ? tr("Chiudi") : tr("✕ Chiudi")}
        </button>
      </div>
      <iframe
        ref={iframeRef}
        src="/icone-giocatori/generatore-icone-giocatori.html"
        title={tr("Generatore icone giocatori")}
        className="flex-1 w-full bg-bg"
      />
    </div>
  );
}
